import type { TerminalOutputItemSchema } from '@terra/shared-dto-chat-types';
import {
  type TerraUIMessage,
  type ToolCallGenericMessage,
  DataMessageTypeEnum,
  ExternalToolResultsResumeSchema,
  parseTerminalOutputItem,
  RUN_COMMAND_TOOL_NAME,
  TERMINAL_TOOL_NAMES,
  isTerminalToolName,
  TerminalOutputResumeSchema,
} from '@terra/shared-dto-chat-types';
import type { z } from 'zod';
import {
  TOOL_CALL_STATUS_PRIORITY,
  hasValue,
  isToolCallData,
  mergeToolCallData,
} from './tool-call-data-merge';
import type {
  ProcessedMessage,
  ProcessedPart,
  ToolCallPartLocation,
} from './message-processing-types';

const EXECUTABLE_TOOL_NAMES_FOR_LIFECYCLE_FALLBACK = new Set<string>([
  'coder-tool',
  'python-tool',
  'http-tool',
  'dynamic-http-tool',
  'validated-http-tool',
  'http-executor',
  ...TERMINAL_TOOL_NAMES,
]);

const TerminalOutputMetadataSchema = TerminalOutputResumeSchema;
const ExternalToolResultsMetadataSchema = ExternalToolResultsResumeSchema;

function terminalOutputToToolResult(
  metadata: z.infer<typeof TerminalOutputItemSchema>,
): ToolCallGenericMessage {
  const status = metadata.exitCode === 0 ? 'completed' : 'error';

  return {
    id: `terminal-output-${metadata.toolCallId}`,
    type:
      status === 'completed'
        ? DataMessageTypeEnum.ToolCallComplete
        : DataMessageTypeEnum.ToolCallError,
    toolCallId: metadata.toolCallId,
    toolName: RUN_COMMAND_TOOL_NAME,
    status,
    timestamp: 0,
    message:
      status === 'completed'
        ? `Command executed successfully (exit code: ${metadata.exitCode})`
        : `Command completed with exit code: ${metadata.exitCode}`,
    input: { command: metadata.command },
    output: metadata.output,
  };
}

function getTerminalOutputToolResults(
  message: TerraUIMessage,
): ToolCallGenericMessage[] {
  const parsedTerminalOutput = TerminalOutputMetadataSchema.safeParse(
    message.metadata,
  );

  if (parsedTerminalOutput.success) {
    return [terminalOutputToToolResult(parsedTerminalOutput.data)];
  }

  const parsedExternalResults = ExternalToolResultsMetadataSchema.safeParse(
    message.metadata,
  );

  if (!parsedExternalResults.success) {
    return [];
  }

  return parsedExternalResults.data.results
    .filter((result) => isTerminalToolName(result.toolName))
    .map((result) => parseTerminalOutputItem(result.result))
    .filter((result): result is NonNullable<typeof result> => !!result)
    .map((result) => terminalOutputToToolResult(result));
}

function mergeToolCallIntoResultsById({
  resultsById,
  dataMessage,
}: {
  resultsById: Record<string, ToolCallGenericMessage>;
  dataMessage: ToolCallGenericMessage;
}) {
  const { toolCallId } = dataMessage;

  if (!toolCallId) return;

  const existing = resultsById[toolCallId];

  if (!existing) {
    resultsById[toolCallId] = dataMessage;

    return;
  }

  const existingPriority = TOOL_CALL_STATUS_PRIORITY[existing.status] ?? -1;
  const newPriority = TOOL_CALL_STATUS_PRIORITY[dataMessage.status] ?? -1;

  resultsById[toolCallId] =
    newPriority >= existingPriority
      ? mergeToolCallData({ anchor: existing, update: dataMessage })
      : mergeToolCallData({ anchor: dataMessage, update: existing });
}

function collectToolCallsFromProcessedParts({
  processedParts,
  resultsById,
}: {
  processedParts: ProcessedPart[];
  resultsById: Record<string, ToolCallGenericMessage>;
}) {
  for (const processedPart of processedParts) {
    if (processedPart.kind !== 'data-message' || !processedPart.dataMessage) {
      continue;
    }

    const dataMessage = processedPart.dataMessage;

    if (!isToolCallData(dataMessage)) {
      continue;
    }

    mergeToolCallIntoResultsById({ resultsById, dataMessage });
  }
}

export function buildToolResultsById(
  processedMessages: ProcessedMessage[],
): Record<string, ToolCallGenericMessage> {
  const resultsById: Record<string, ToolCallGenericMessage> = {};

  for (const processedMessage of processedMessages) {
    const terminalOutputToolResults = getTerminalOutputToolResults(
      processedMessage.message,
    );

    for (const terminalOutputToolResult of terminalOutputToolResults) {
      resultsById[terminalOutputToolResult.toolCallId] =
        terminalOutputToolResult;
    }

    collectToolCallsFromProcessedParts({
      processedParts: processedMessage.parts,
      resultsById,
    });
  }

  return resultsById;
}

export function mergeToolCallPartsAcrossMessages(
  processedMessages: ProcessedMessage[],
): ProcessedMessage[] {
  const nextMessages = processedMessages.map((processedMessage) => ({
    ...processedMessage,
    parts: [...processedMessage.parts],
  }));
  const firstToolCallLocationById = new Map<string, ToolCallPartLocation>();
  const openToolLocationByName = new Map<string, ToolCallPartLocation>();

  const readToolDataAtLocation = (
    location: ToolCallPartLocation | undefined,
  ): ToolCallGenericMessage | undefined => {
    if (!location) return undefined;
    const message = nextMessages[location.messageIndex];
    const part = message?.parts[location.partIndex];

    if (
      part?.kind !== 'data-message' ||
      !part.dataMessage ||
      !isToolCallData(part.dataMessage)
    ) {
      return undefined;
    }

    return part.dataMessage;
  };

  const isTerminalLifecycleStatus = (status: string | undefined): boolean =>
    status === 'completed' || status === 'error' || status === 'aborted';

  const hasFinalExecutableResult = (
    toolData: ToolCallGenericMessage | undefined,
  ): boolean => {
    if (!toolData) {
      return false;
    }

    if (hasValue(toolData.error)) {
      return true;
    }

    const toolOutput = toolData.output;

    if (!toolOutput || typeof toolOutput !== 'object') {
      return hasValue(toolOutput);
    }

    const outputRecord = toolOutput as Record<string, unknown>;
    const toolName = toolData.toolName ?? '';

    if (toolName === 'coder-tool' || toolName === 'python-tool') {
      const nestedOutput =
        outputRecord.output && typeof outputRecord.output === 'object'
          ? (outputRecord.output as Record<string, unknown>)
          : undefined;

      return Boolean(
        nestedOutput ||
          hasValue(outputRecord.result) ||
          hasValue(outputRecord.success),
      );
    }

    if (
      toolName === 'http-tool' ||
      toolName === 'dynamic-http-tool' ||
      toolName === 'validated-http-tool' ||
      toolName === 'http-executor'
    ) {
      const response =
        outputRecord.response && typeof outputRecord.response === 'object'
          ? (outputRecord.response as Record<string, unknown>)
          : undefined;

      return Boolean(
        response ||
          hasValue(outputRecord.statusCode) ||
          hasValue(outputRecord.status),
      );
    }

    if (isTerminalToolName(toolName)) {
      return Boolean(
        hasValue(outputRecord.exitCode) ||
          hasValue(outputRecord.stdout) ||
          hasValue(outputRecord.stderr) ||
          hasValue(outputRecord.output),
      );
    }

    return true;
  };

  const shouldUseFallbackByName = ({
    toolName,
    updateData,
    fallbackLocation,
  }: {
    toolName: string;
    updateData: ToolCallGenericMessage;
    fallbackLocation: ToolCallPartLocation | undefined;
  }): boolean => {
    if (!EXECUTABLE_TOOL_NAMES_FOR_LIFECYCLE_FALLBACK.has(toolName)) {
      return false;
    }

    const fallbackData = readToolDataAtLocation(fallbackLocation);

    if (!fallbackData) {
      return false;
    }

    const toolCallId = updateData.toolCallId;
    const hasMismatchedToolCallIds =
      !!toolCallId &&
      !!fallbackData.toolCallId &&
      toolCallId !== fallbackData.toolCallId;

    if (hasMismatchedToolCallIds) {
      const updateHasInput = hasValue(updateData.input);
      const fallbackHasInput = hasValue(fallbackData.input);

      if (updateHasInput && fallbackHasInput) {
        return false;
      }

      if (isTerminalLifecycleStatus(fallbackData.status)) {
        const fallbackOutput =
          fallbackData.output && typeof fallbackData.output === 'object'
            ? (fallbackData.output as Record<string, unknown>)
            : undefined;
        const fallbackHasPlaceholderPayload =
          fallbackHasInput ||
          hasValue(fallbackOutput?.script) ||
          hasValue(fallbackOutput?.code);

        if (!fallbackHasPlaceholderPayload) {
          return false;
        }
      }
    }

    const fallbackLooksClosed =
      isTerminalLifecycleStatus(fallbackData.status) &&
      hasFinalExecutableResult(fallbackData);

    return !fallbackLooksClosed;
  };

  for (
    let messageIndex = 0;
    messageIndex < nextMessages.length;
    messageIndex++
  ) {
    const processedMessage = nextMessages[messageIndex];

    for (
      let partIndex = 0;
      partIndex < processedMessage.parts.length;
      partIndex++
    ) {
      const processedPart = processedMessage.parts[partIndex];

      if (processedPart.kind !== 'data-message') {
        continue;
      }

      if (
        !processedPart.dataMessage ||
        !isToolCallData(processedPart.dataMessage)
      ) {
        continue;
      }

      const toolCallId = processedPart.dataMessage.toolCallId;
      const toolName = processedPart.dataMessage.toolName ?? '';
      const fallbackLocation = toolName
        ? openToolLocationByName.get(toolName)
        : undefined;

      let firstLocation = toolCallId
        ? firstToolCallLocationById.get(toolCallId)
        : undefined;

      if (
        !firstLocation &&
        shouldUseFallbackByName({
          toolName,
          updateData: processedPart.dataMessage,
          fallbackLocation,
        })
      ) {
        firstLocation = fallbackLocation;
      }

      if (!firstLocation) {
        if (toolCallId) {
          firstToolCallLocationById.set(toolCallId, {
            messageIndex,
            partIndex,
          });
        }

        if (toolName) {
          openToolLocationByName.set(toolName, { messageIndex, partIndex });
        }

        continue;
      }

      const firstMessage = nextMessages[firstLocation.messageIndex];
      const firstPart = firstMessage?.parts[firstLocation.partIndex];

      if (
        !firstPart ||
        firstPart.kind !== 'data-message' ||
        !firstPart.dataMessage ||
        !isToolCallData(firstPart.dataMessage)
      ) {
        if (toolCallId) {
          firstToolCallLocationById.set(toolCallId, {
            messageIndex,
            partIndex,
          });
        }

        if (toolName) {
          openToolLocationByName.set(toolName, { messageIndex, partIndex });
        }

        continue;
      }

      const mergedToolData = mergeToolCallData({
        anchor: firstPart.dataMessage,
        update: processedPart.dataMessage,
      });

      firstMessage.parts[firstLocation.partIndex] = {
        ...firstPart,
        dataMessage: mergedToolData,
      };

      processedMessage.parts[partIndex] = {
        ...processedPart,
        kind: 'skip',
      };

      if (toolCallId) {
        firstToolCallLocationById.set(toolCallId, firstLocation);
      }

      if (mergedToolData.toolCallId) {
        firstToolCallLocationById.set(mergedToolData.toolCallId, firstLocation);
      }

      if (toolName) {
        openToolLocationByName.set(toolName, firstLocation);
      }
    }
  }

  return nextMessages;
}
